import React from "react";

import {
  addAssetWatch, bindSingleAssetAgentView,
  createSingleAssetAgentConversation, extractSingleAssetAgentImportText,
  getAssetTrackingStatus, getSingleAssetAgentConversation,
  getSingleAssetAgentMarketNews, getSingleAssetAgentMarketQuote,
  getSingleAssetBindingView, getSingleAssetMarketView,
  getStockDetail, parseSingleAssetMessage, randomId, removeAssetWatch,
} from "../../api/client";
import { isStockAssetType } from "../../utils/assetType";
import { decimalMax4, money, percent } from "../../utils/format";
import { assertSupportedTradeImportFile, readTradeImportText, TRADE_IMPORT_ACCEPT } from "../../utils/importFiles";
import { agentMessageDisplayContent } from "../../utils/agentImportMessage";
import { INVESTMENT_AGENT_PANEL_WIDTH_KEY, useAgentPanelLayout } from "../../hooks/useAgentPanelLayout";
import styles from "./singleAssetAgentDev.module.css";
import { SingleAssetDialogs } from "./SingleAssetDialogs";
import { SingleAssetPrototypeResult } from "./SingleAssetPrototypeResult";
import { SingleAssetTrackingAction } from "./SingleAssetTrackingAction";
import resultStyles from "./singleAssetResult.module.css";
import { accountingViewFromBinding, AgentChatPanel, WorkspaceHeader } from "./SingleAssetAgentPanels";
import { AGENT_PANEL_ENABLED } from "../../agentPanel";
import { display, formatTurnMetrics, historyMessage, isSystemDataIssue, nowLabel } from "./agentMessageFormat";
import { loadTrackedHoldingView } from "./singleAssetBindingNavigation.js";
import { bindingViewRecoveryMessage, resolveSingleAssetViewState, shouldAdoptRouteBindingView } from "./singleAssetViewState.js";
import { useSingleAssetMarketView } from "./useSingleAssetMarketView.js";
import { useSingleAssetSession } from "./useSingleAssetSession.js";

const ACTIVE_CONVERSATION_KEY = "single_asset_agent_active_conversation";
export function SingleAssetAgentDev({
  initialConversationId = null,
  initialBindingId = null,
  initialAssetQuery = null,
  initialHoldingId = null,
  initialWorkspaceId = null,
  initialAgentHandoff = null,
  initialMarketView = null,
  initialBindingView = null,
  initialOpenHoldingEntry = false,
  onBack = null,
  onLogout,
  onResultOpen = null,
  onScreenerOpen = null,
  onAgentStateChange = null,
  onSessionRouteChange = null,
  onEmptyWorkspace = null,
  embedded = false,
}) {
  const initialTurnResult = initialAgentHandoff?.turnResult ?? null;
  const [conversationId, setConversationId] = React.useState(initialTurnResult?.conversation_id ?? null);
  const [revision, setRevision] = React.useState(initialTurnResult?.revision ?? null);
  const [rows, setRows] = React.useState(initialTurnResult?.rows ?? []);
  const [messages, setMessages] = React.useState(initialAgentHandoff?.messages ?? []);
  const [input, setInput] = React.useState("");
  const [turnResult, setTurnResult] = React.useState(initialTurnResult);
  const [directMarketView, setDirectMarketView] = React.useState(initialMarketView);
  const [directAccountingView, setDirectAccountingView] = React.useState(
    accountingViewFromBinding(initialBindingView),
  );
  const [activeBinding, setActiveBinding] = React.useState(
    initialBindingView?.binding ?? initialTurnResult?.binding ?? null,
  );
  const [retainedWorkspace, setRetainedWorkspace] = React.useState(null);
  const [liveContext, setLiveContext] = React.useState(null);
  const [stockDetail, setStockDetail] = React.useState(null);
  const [liveContextLoading, setLiveContextLoading] = React.useState(false);
  const [newsLoading, setNewsLoading] = React.useState(false);
  const [requestError, setRequestError] = React.useState("");
  const [fileImportError, setFileImportError] = React.useState("");
  const [fileImporting, setFileImporting] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [activityLabel, setActivityLabel] = React.useState("Agent 正在解析…");
  const [holdingEditDialogOpen, setHoldingEditDialogOpen] = React.useState(
    Boolean(initialAgentHandoff?.holdingEditProposal),
  );
  // 打字那一路解析出来的事实：propose 档只解析不落库，结果先进弹窗给用户核对。
  const [holdingEditProposal, setHoldingEditProposal] = React.useState(
    initialAgentHandoff?.holdingEditProposal ?? null,
  );
  const [trackedHolding, setTrackedHolding] = React.useState(null);
  const [watching, setWatching] = React.useState(false);
  const [trackingBusy, setTrackingBusy] = React.useState(false);
  const [restoringConversation, setRestoringConversation] = React.useState(
    Boolean(!initialTurnResult && (initialConversationId
      || window.sessionStorage.getItem(ACTIVE_CONVERSATION_KEY)),
    ),
  );
  const threadRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const composerInputRef = React.useRef(null);
  const requestSequenceRef = React.useRef(0);
  // 概览内嵌形态下，提交完要跳去完整工作台。但不能立刻跳——一跳弹窗就被卸载，
  // 「已记录 / 有 N 条提醒」跟着一起没了。存在这里，等用户点「关闭」再跳。
  const pendingResultHandoffRef = React.useRef(null);
  const activeSlotRef = React.useRef({
    conversationId: initialTurnResult?.conversation_id ?? initialConversationId,
    bindingId: initialBindingView?.binding?.binding_id
      ?? initialTurnResult?.binding?.binding_id
      ?? initialBindingId,
  });
  const { agentPanelWidth, startAgentPanelResize } = useAgentPanelLayout({
    isDashboard: false,
    initialWidth: 420,
    centerMinWidth: 900,
    rightRailWidth: 0,
    storageKey: INVESTMENT_AGENT_PANEL_WIDTH_KEY,
  });
  // 会话恢复是异步的。带 ?asset= 进来时必须等它落地再发问，否则 conversationId
  // 还是 null，这一问会另开一个会话，用户回到概览就看不见自己原来那条线了。
  const restoringConversationRef = React.useRef(
    Boolean(!initialTurnResult && (initialConversationId
      || window.sessionStorage.getItem(ACTIVE_CONVERSATION_KEY)),
    ),
  );
  const loadedMarketAssetRef = React.useRef(initialMarketView?.asset_code ?? null);
  const loadedHoldingAssetRef = React.useRef(
    initialBindingView?.target?.asset_code ?? null,
  );

  React.useEffect(() => {
    const requestedConversationId = initialConversationId
      || window.sessionStorage.getItem(ACTIVE_CONVERSATION_KEY);
    if (!requestedConversationId) {
      restoringConversationRef.current = false;
      setRestoringConversation(false);
      return undefined;
    }
    restoringConversationRef.current = true;
    setRestoringConversation(true);
    let cancelled = false;
    void getSingleAssetAgentConversation(
        requestedConversationId,
        { historyLimit: 200 },
      )
      .then((latest) => {
        if (cancelled) return;
        restoringConversationRef.current = false;
        setRestoringConversation(false);
        setConversationId(latest.conversation_id);
        activeSlotRef.current = {
          ...activeSlotRef.current,
          conversationId: latest.conversation_id,
        };
        setRevision(latest.revision);
        setRows(latest.rows ?? []);
        setTurnResult(latest);
        setMessages((latest.history ?? []).map(historyMessage));
      })
      .catch((error) => {
        if (cancelled) return;
        restoringConversationRef.current = false;
        setRestoringConversation(false);
        setRequestError(error.message || "无法加载核算会话。");
        if (!initialConversationId) {
          window.sessionStorage.removeItem(ACTIVE_CONVERSATION_KEY);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initialConversationId]);

  // 明确代码的页面下钻不需要模型判断意图；直接读取确定性行情视图。
  useSingleAssetMarketView({
    initialAssetQuery, activeBinding, directAccountingView, loadedMarketAssetRef,
    setDirectMarketView, onError: setRequestError,
  });

  React.useEffect(() => {
    if (!shouldAdoptRouteBindingView(initialBindingView, initialBindingId)) return;
    setDirectAccountingView(accountingViewFromBinding(initialBindingView));
    setActiveBinding(initialBindingView?.binding ?? null);
    setRequestError(bindingViewRecoveryMessage(initialBindingView));
    loadedHoldingAssetRef.current = initialBindingView?.target?.asset_code ?? null;
    if (initialBindingView?.binding?.binding_id) {
      activeSlotRef.current = {
        conversationId: initialBindingView.binding.conversation_id,
        bindingId: initialBindingView.binding.binding_id,
      };
    }
  }, [initialBindingView, initialBindingId]);

  // 概览持有标的下钻只切换右侧核算对象。左侧仍使用当前 Agent 会话，
  // 不把持仓来源会话错误地当成用户正在继续的聊天。
  React.useEffect(() => {
    const bindingId = String(initialBindingId || "").trim();
    const targetId = String(initialHoldingId || initialWorkspaceId || "").trim();
    if (!bindingId || !conversationId) return undefined;
    if (
      activeBinding?.binding_id === bindingId
      && (
        directAccountingView
        || activeBinding.write_mode === "create_on_write"
        // 概览已交接 market binding；它没有核算事实可恢复。行情由
        // useSingleAssetMarketView 读取，再请求 binding view 只会生成同一份大响应。
        || (activeBinding.view_type === "market" && activeBinding.write_mode === "read_only")
      )
    ) return undefined;
    let cancelled = false;
    const sequence = ++requestSequenceRef.current;
    void getSingleAssetBindingView(conversationId, bindingId)
      .then((view) => {
        if (cancelled) return;
        if (sequence !== requestSequenceRef.current) return;
        if (targetId && view.target?.id && view.target.id !== targetId) return;
        activeSlotRef.current = { conversationId, bindingId };
        setActiveBinding(view.binding);
        loadedHoldingAssetRef.current = view.target?.asset_code ?? null;
        setDirectAccountingView(accountingViewFromBinding(view));
        setRequestError(bindingViewRecoveryMessage(view));
      })
      .catch((error) => {
        if (!cancelled) setRequestError(error.message || "暂时无法打开该持仓。");
      });
    return () => { cancelled = true; };
  }, [conversationId, initialBindingId, initialHoldingId, initialWorkspaceId]);

  React.useLayoutEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    thread.scrollTop = thread.scrollHeight;
  }, [messages, busy]);

  React.useEffect(() => {
    onAgentStateChange?.({ turnResult, messages });
  }, [messages, onAgentStateChange, turnResult]);

  React.useEffect(() => {
    activeSlotRef.current = {
      conversationId,
      bindingId: activeBinding?.binding_id ?? initialBindingId ?? null,
    };
  }, [activeBinding?.binding_id, conversationId, initialBindingId]);

  const { calculationResult, marketView, displayResult, activeViewType, accountingResultReady } = resolveSingleAssetViewState({
    activeBinding, directAccountingView, directMarketView, retainedWorkspace, turnResult,
  });
  React.useEffect(() => {
    const result = displayResult;
    const code = String(result?.asset_code || "").toUpperCase();
    if (!code || (!marketView && !accountingResultReady)) {
      setLiveContext(null);
      setStockDetail(null);
      setLiveContextLoading(false);
      setNewsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const isOpenFund = code.endsWith(".OF");
    const isStock = isStockAssetType(result?.asset_type);
    setLiveContextLoading(!isOpenFund);
    setNewsLoading(isStock);
    setLiveContext(null);
    setStockDetail(null);
    const mergeLiveContext = (payload) => setLiveContext((current) => ({
      meta: { ...(current?.meta ?? {}), ...(payload?.meta ?? {}) },
      data: { ...(current?.data ?? {}), ...(payload?.data ?? {}) },
    }));
    if (!isOpenFund) {
      void getSingleAssetAgentMarketQuote(code).then((payload) => {
        if (!cancelled) mergeLiveContext(payload);
      }).catch(() => {}).finally(() => { if (!cancelled) setLiveContextLoading(false); });
    }
    if (isStock) {
      void getSingleAssetAgentMarketNews(code).then((payload) => {
        if (!cancelled) mergeLiveContext(payload);
      }).catch(() => {}).finally(() => { if (!cancelled) setNewsLoading(false); });
      void getStockDetail({ code, compact: true }).then((payload) => {
        if (!cancelled) setStockDetail(payload);
      }).catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [accountingResultReady, activeViewType, displayResult?.asset_code, displayResult?.asset_type]);

  const trackedAssetCode = String(
    displayResult?.asset_code
      || turnResult?.active_draft?.asset_code
      || "",
  ).toUpperCase();

  React.useEffect(() => {
    if (!trackedAssetCode) {
      setTrackedHolding(null);
      setWatching(false);
      return undefined;
    }
    let cancelled = false;
    void getAssetTrackingStatus(trackedAssetCode)
      .then((status) => {
        if (cancelled) return;
        setTrackedHolding(status.holding ?? null);
        setWatching(Boolean(status.watching));
      })
      .catch(() => {
        if (!cancelled) {
          setTrackedHolding(null);
          setWatching(false);
        }
      });
    return () => { cancelled = true; };
  }, [trackedAssetCode]);

  async function toggleWatch() {
    if (!trackedAssetCode || trackingBusy) return;
    setTrackingBusy(true);
    setRequestError("");
    try {
      if (watching) {
        await removeAssetWatch(trackedAssetCode);
        setWatching(false);
      } else {
        await addAssetWatch(trackedAssetCode);
        setWatching(true);
      }
    } catch (error) {
      setRequestError(error.message || "关注状态未能更新。");
    } finally {
      setTrackingBusy(false);
    }
  }

  async function viewTrackedHolding() {
    if (trackingBusy || !conversationId || !trackedHolding?.holding_id) return;
    setTrackingBusy(true);
    setRequestError("");
    try {
      const { binding, bindingView, revision: nextRevision } = await loadTrackedHoldingView(
        conversationId, revision, trackedHolding,
      );
      setRevision(nextRevision);
      setTurnResult((current) => current ? {
        ...current,
        revision: nextRevision,
        current_binding_id: binding.binding_id,
      } : current);
      setActiveBinding(binding);
      activeSlotRef.current = { conversationId, bindingId: binding.binding_id };
      setDirectMarketView(null);
      setDirectAccountingView(accountingViewFromBinding(bindingView));
      setRequestError(bindingViewRecoveryMessage(bindingView));
      setRetainedWorkspace(null);
      onSessionRouteChange?.({
        conversationId,
        bindingId: binding.binding_id,
        viewMode: binding.view_type,
        assetCode: binding.asset_code,
        targetId: binding.target_id,
      });
    } catch (error) {
      setRequestError(error.message || "暂时无法打开该持仓。");
    } finally {
      setTrackingBusy(false);
    }
  }

  async function submitMessage(
    rawMessage,
    {
      fromFile = false,
      activity = "Agent 正在解析…",
      bindingOverride = null,
      conversationRevisionOverride = null,
    } = {},
  ) {
    const message = rawMessage.trim();
    if (!message || busy || session.sessionBusy || (fileImporting && !fromFile)) return;
    const targetAssetCode = String(
      displayResult?.asset_code || "",
    ).trim() || null;
    const userTurn = { role: "user", content: message, time: nowLabel() };
    const nextMessages = [...messages, userTurn];
    setMessages(nextMessages);
    setInput("");
    setRequestError("");
    setFileImportError("");
    setActivityLabel(activity);
    // Keep the last trusted calculation visible while the next Agent turn is
    // running. Clearing only calculation_result while retaining status=ready
    // briefly rendered the generic "calculation unavailable" error even
    // though the deterministic calculation had not failed.
    setBusy(true);
    try {
      const startedAt = performance.now();
      const requestId = ++requestSequenceRef.current;
      const effectiveBinding = bindingOverride ?? activeBinding;
      // /parse 要求会话已经存在（conversation_id 与 expected_revision 必填），
      // 不像 /turn 那样能顺手建一个。空会话里的第一句话先补一次创建。
      let turnConversationId = conversationId;
      let turnRevision = conversationRevisionOverride ?? revision;
      if (!turnConversationId) {
        const fresh = await createSingleAssetAgentConversation();
        turnConversationId = fresh.conversation_id;
        turnRevision = fresh.revision;
        setConversationId(turnConversationId);
        window.sessionStorage.setItem(ACTIVE_CONVERSATION_KEY, turnConversationId);
        activeSlotRef.current = {
          ...activeSlotRef.current,
          conversationId: turnConversationId,
        };
      }
      // 打字只解析、不落库：解析结果进「记录交易」弹窗，用户核对后由结构化端点写入。
      const result = await parseSingleAssetMessage(message, {
        conversationId: turnConversationId,
        expectedRevision: turnRevision,
        bindingId: effectiveBinding?.binding_id ?? null,
        targetAssetCode,
      });
      const elapsedMs = Math.round(performance.now() - startedAt);
      // 迟到的响应不许改界面：用户可能已经切走或又发了一句。
      if (requestId !== requestSequenceRef.current) return;
      const answeredMessages = [
        ...nextMessages,
        {
          role: "agent",
          content: result.assistant_message,
          time: nowLabel(),
          metrics: formatTurnMetrics(result.timing, result.llm_usage, elapsedMs),
        },
      ];
      setMessages(answeredMessages);
      if (result.proposed_facts) {
        const proposalAssetCode = String(result.proposed_facts.asset_code || "").toUpperCase();
        const binding = await bindSingleAssetAgentView(
          turnConversationId,
          result.revision,
          result.existing_holding?.holding_id
            ? {
              type: "holding",
              target_id: result.existing_holding.holding_id,
              asset_code: proposalAssetCode,
            }
            : { type: "market", asset_code: proposalAssetCode },
        );
        const bindingView = await getSingleAssetBindingView(
          turnConversationId, binding.binding_id,
        );
        if (requestId !== requestSequenceRef.current) return;

        const proposal = result.proposed_facts;
        const nextTurnResult = {
          ...result,
          revision: binding.conversation_revision,
          current_binding_id: binding.binding_id,
          binding,
          market_view: bindingView.market_view ?? null,
        };
        if (embedded) {
          onResultOpen?.({
            turnResult: nextTurnResult,
            messages: answeredMessages,
            holdingEditProposal: proposal,
          });
          return;
        }
        setRevision(binding.conversation_revision);
        setTurnResult(nextTurnResult);
        setActiveBinding(binding);
        activeSlotRef.current = {
          conversationId: turnConversationId,
          bindingId: binding.binding_id,
        };
        setDirectAccountingView(accountingViewFromBinding(bindingView));
        setDirectMarketView(bindingView.market_view ?? null);
        setRetainedWorkspace(null);
        onSessionRouteChange?.({
          conversationId: turnConversationId,
          bindingId: binding.binding_id,
          viewMode: binding.view_type,
          assetCode: binding.asset_code,
          targetId: binding.target_id,
        });
        setHoldingEditProposal(proposal);
        setHoldingEditDialogOpen(true);
        return;
      }
      setRevision(result.revision);
      // 「找一类标的」：Agent 只转交那句话，右侧换成「添加关注」页去查。
      if (result.screener_query && onScreenerOpen) {
        onScreenerOpen(result.screener_query);
        return;
      }
      // 只打了一个标的名/代码时，后端不解析、直接回这只票的行情，右侧切过去。
      // 必须同时清掉核算绑定：resolveSingleAssetViewState 里绑定优先于
      // directMarketView，只 set 行情的话右侧不会动（用户看到的是"没反应"）。
      if (result.market_view) {
        // 概览内嵌的 Agent 没有右侧结果区，行情得随路由交给完整工作台，
        // 走的是「关注列表点开一只标的」同一条路（?asset= + 路由态行情）。
        if (embedded) {
          onResultOpen?.({
            turnResult: {
              conversation_id: turnConversationId,
              market_view: result.market_view,
            },
            messages: answeredMessages,
          });
          return;
        }
        loadedMarketAssetRef.current = result.market_view.asset_code;
        setActiveBinding(null);
        setDirectAccountingView(null);
        setRetainedWorkspace(null);
        setDirectMarketView(result.market_view);
        onSessionRouteChange?.({
          conversationId: turnConversationId,
          bindingId: null,
          viewMode: "market",
          assetCode: result.market_view.asset_code,
          targetId: null,
        });
      }
    } catch (error) {
      if (error.status === 409 && conversationId) {
        try {
          const latest = await getSingleAssetAgentConversation(conversationId, { historyLimit: 200 });
          setRevision(latest.revision);
          setRows(latest.rows ?? []);
          setTurnResult(latest);
          // 服务端历史里没有这一句——它还没落库。直接整体覆盖会把用户刚打出来的
          // 那句话连同上下文一起抹掉：用户看到的是"我发出去的字凭空消失了"，
          // 而不是"版本冲突"（AGENT-046 / NL-REG-032，用户手动发现）。
          // 保留它，并把原文放回输入框，用户不必凭记忆重打。
          setMessages([...(latest.history ?? []).map(historyMessage), userTurn]);
          setInput(message);
          setRequestError(
            "会话在别处更新了，已同步到最新状态。你刚才那句我留在输入框里，直接再发一次即可。",
          );
          return;
        } catch {
          // Fall through to the ordinary error state if the refresh also fails.
        }
      }
      setRequestError(error.message);
      setTurnResult((current) => ({
        ...(current ?? {}),
        status: current?.status ?? (rows.length ? "ready" : "empty"),
        calculation_result: null,
        calculation_status: "failed",
        calculation_failure: {
          reason: error.message,
          required_actions: ["请稍后重试；当前草稿已保留。"],
          user_correctable: false,
        },
      }));
      setMessages((current) => [
        ...current,
        {
          role: "agent",
          content: `本轮没有完成：${error.message}。当前交易草稿已保留，可以换一种说法继续补充。`,
          time: nowLabel(),
        },
      ]);
    } finally {
      setBusy(false);
      setActivityLabel("Agent 正在解析…");
    }
  }

  async function submit() {
    await submitMessage(input);
  }

  function openFilePicker() {
    if (interactionBusy) return;
    fileInputRef.current?.click();
  }

  async function importFile(file) {
    if (!file || interactionBusy) return;
    setFileImporting(true);
    setFileImportError("");
    setRequestError("");
    try {
      const extension = assertSupportedTradeImportFile(file);
      const extracted = extension === ".xlsx"
        ? await extractSingleAssetAgentImportText(file)
        : { text: await readTradeImportText(file) };
      const text = String(extracted.text ?? "").trim();
      if (!text) throw new Error("文件中没有可供 Agent 解析的内容。");
      await submitMessage(`导入文件：${file.name}\n\n${text}`, { fromFile: true });
    } catch (error) {
      setFileImportError(`文件导入失败：${error.message}`);
    } finally {
      setFileImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const workspaceTurnResult = directAccountingView ?? turnResult;
  const status = directAccountingView?.status
    ?? (retainedWorkspace?.calculationResult
    && !turnResult?.calculation_result
    ? "ready"
    : turnResult?.status ?? (calculationResult ? "ready" : "empty"));
  const displayAssetCode = String(displayResult?.asset_code || "").toUpperCase();
  const displayRows = directAccountingView?.rows ?? rows;
  const systemDataBlocked = (workspaceTurnResult?.issues ?? []).some(isSystemDataIssue);
  // Binding 返回了确定性结果就必须展示；会话 status 不能再次否决权威 Holding。
  const hasResult = Boolean(marketView || calculationResult);
  const hasStarted = messages.length > 0 || busy || displayRows.length > 0
    || Boolean(marketView) || Boolean(calculationResult)
    || (Boolean(turnResult) && status !== "empty")
    || Boolean(initialAssetQuery) || Boolean(initialHoldingId) || Boolean(initialWorkspaceId);
  const showingRetainedWorkspace = Boolean(
    retainedWorkspace
    && !turnResult?.market_view
    && !turnResult?.calculation_result,
  );
  const trackedHoldingLoaded = Boolean(
    activeViewType === "holding"
    && trackedHolding?.holding_id
    && trackedHolding.holding_id === (
      directAccountingView?.target?.id ?? activeBinding?.target_id
    )
  );
  const trackedMetrics = trackedHolding?.metrics ?? {};
  const pendingConfirmation = turnResult?.confirmation_bundle;
  const pendingAction = turnResult?.pending_action, allowedActions = turnResult?.allowed_actions ?? [];
  const pendingCandidate = turnResult?.pending_candidate || turnResult?.replacement_candidate;
  const hasPendingAgentDecision = Boolean(pendingConfirmation || pendingAction || pendingCandidate || allowedActions.includes("confirm"));
  // 会话生命周期（新建 / 切换历史会话 / 退出登录）。界面入口已在 2026-08-31 随
  // 「会话管理」下拉一起删掉，能力保留在 hook 里，怎么接回来见该文件头注释。
  const session = useSingleAssetSession({
    activeBinding, activeSlotRef, busy, calculationResult, directAccountingView,
    displayAssetCode, fileImporting, hasPendingAgentDecision, input, marketView,
    onLogout, onSessionRouteChange, setActiveBinding, setConversationId,
    setDirectAccountingView, setDirectMarketView, setInput, setMessages,
    setRequestError, setRetainedWorkspace, setRevision, setRows, setTurnResult,
    activeConversationKey: ACTIVE_CONVERSATION_KEY,
  });
  const interactionBusy = busy || session.sessionBusy || fileImporting;

  React.useEffect(() => {
    if (!hasPendingAgentDecision) return undefined;
    const guard = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [hasPendingAgentDecision]);

  React.useEffect(() => {
    if (!embedded && !restoringConversation && !hasStarted && !initialAssetQuery && !initialBindingId && !initialHoldingId && !initialWorkspaceId) {
      onEmptyWorkspace?.();
    }
  }, [embedded, hasStarted, initialAssetQuery, initialBindingId, initialHoldingId, initialWorkspaceId, onEmptyWorkspace, restoringConversation]);

  function openHoldingEdit() {
    // 手点进来的是空表单；上一轮打字留下的提案不能跟着复现。
    setHoldingEditProposal(null);
    setHoldingEditDialogOpen(true);
  }

  // 从概览「新增持仓」选中标的进来的：用户要的是录入，不是看行情。
  // 只跳转不弹窗等于把他撂在半路——还得自己找到「记录交易」再点一次。
  // 只在首次挂载时开一次：之后用户自己关掉弹窗，不该被这个意图再弹回来。
  const holdingEntryOpened = React.useRef(false);
  React.useEffect(() => {
    if (!initialOpenHoldingEntry || holdingEntryOpened.current) return;
    holdingEntryOpened.current = true;
    openHoldingEdit();
  }, [initialOpenHoldingEntry]);

  function renderTrackingAction() {
      return (
        <SingleAssetTrackingAction
          {...{
            activeViewType, calculationResult, showingRetainedWorkspace,
            trackedHolding, trackingBusy, watching,
          }}
          onToggleWatch={() => { void toggleWatch(); }}
          onViewHolding={() => { void viewTrackedHolding(); }}
        />
      );
  }
  const WorkbenchRoot = embedded ? "div" : "main";
  const showChatPanel = AGENT_PANEL_ENABLED && (embedded || hasStarted);
  return (
    <WorkbenchRoot
      className={`${styles.agentWorkbench} ${AGENT_PANEL_ENABLED && hasStarted && !embedded ? styles.withAgent : ""} ${embedded ? styles.embeddedWorkbench : ""}`}
      style={!embedded ? { "--single-agent-width": `${agentPanelWidth}px` } : undefined}
      {...(!embedded ? { id: "main-content" } : {})}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={TRADE_IMPORT_ACCEPT}
        aria-label="选择单标的 Agent 交易文件"
        hidden
        onChange={(event) => { void importFile(event.target.files?.[0]); }}
      />
      {showChatPanel && <AgentChatPanel {...{
        activityLabel, busy, conversationId, embedded, fileImportError, fileImporting,
        hasStarted, input, interactionBusy, messages, trackedHoldingLoaded,
        trackedHolding, trackedMetrics, displayRows, threadRef, composerInputRef, turnResult,
        currentContext: displayResult?.asset_name ? `当前${marketView ? "查看行情" : "核算"}：${displayResult.asset_name}` : embedded ? "当前：概览" : "",
      }} onInput={setInput} onOpenFile={openFilePicker}
      onResize={startAgentPanelResize} onSubmit={() => { void submit(); }} />}

      {!embedded && <section className={`${styles.agentWorkspace} ${!hasStarted ? styles.workspaceEmpty : ""}`}>
        {hasStarted && displayRows.length > 0 && !hasResult && <WorkspaceHeader {...{
          calculationResult, hasResult, rows: displayRows, status, systemDataBlocked, turnResult: workspaceTurnResult,
        }} onRenderAction={renderTrackingAction} onEditHolding={openHoldingEdit} />}
        {hasStarted && <div className={styles.workspaceBody}>
          {hasResult && (
            <SingleAssetPrototypeResult
              key={`${conversationId}:${directAccountingView?.target?.id ?? "turn"}:${directAccountingView?.target?.revision ?? revision}`}
              result={displayResult}
              liveContext={liveContext}
              detail={stockDetail}
              liveLoading={liveContextLoading}
              newsLoading={newsLoading}
              mode={marketView ? "market" : "accounting"}
              headerAction={renderTrackingAction()}
              onBack={() => onBack?.({ turnResult, messages })}
              onEditHolding={openHoldingEdit}
            />
          )}
          {!hasResult && requestError && (
            <div className={styles.requestError} role="alert">
              <strong>当前结果暂时无法显示</strong>
              <p>{requestError}。当前页面事实没有被清空，可以在左侧继续补充或稍后重试。</p>
            </div>
          )}
        </div>}
      </section>}
      <SingleAssetDialogs
        {...{ activeBinding, activeSlotRef, conversationId, revision }}
        pageView={directAccountingView}
        assetCode={displayResult?.asset_code || trackedAssetCode}
        assetType={displayResult?.asset_type || displayResult?.active_draft?.asset_type || null}
        busy={interactionBusy}
        newSessionOpen={session.newSessionDialogOpen}
        onNewSessionCancel={session.closeNewSessionDialog}
        onNewSessionConfirm={() => { void session.startNewSession(); }}
        holdingEditOpen={holdingEditDialogOpen}
        holdingEditProposal={holdingEditProposal}
        onApplyBindingView={(view) => {
          const accounting = accountingViewFromBinding(view);
          setDirectAccountingView(accounting);
          if (view?.binding) {
            setActiveBinding(view.binding);
            onSessionRouteChange?.({
              conversationId: view.binding.conversation_id ?? conversationId,
              bindingId: view.binding.binding_id,
              viewMode: view.binding.view_type,
              assetCode: view.binding.asset_code,
              targetId: view.binding.target_id,
            });
          }
          // 概览内嵌的 Agent 没有右侧结果区。提交完把结果随路由交给完整工作台，
          // 否则用户在概览里填完表单，页面看上去什么都没发生。
          // 走 /turn 落库时这一步由 result.calculation_result 触发，propose 档
          // 那一轮什么都不写，触发点就只剩这里。
          if (embedded && onResultOpen && accounting) {
            pendingResultHandoffRef.current = {
              turnResult: {
                ...accounting,
                conversation_id: view.binding?.conversation_id ?? conversationId,
              },
              messages,
            };
          }
        }}
        onHoldingEditCancel={() => {
          setHoldingEditDialogOpen(false);
          setHoldingEditProposal(null);
          const handoff = pendingResultHandoffRef.current;
          pendingResultHandoffRef.current = null;
          if (handoff) onResultOpen?.(handoff);
        }}
        onSetActiveBinding={setActiveBinding}
        onSetRevision={setRevision}
      />
    </WorkbenchRoot>
  );
}
